import { TextureData } from 'weatherlayers-gl';

export interface BufferedCacheSettings {
  readonly keyFrameInterval: number;
}

export interface BufferedFrameCache {
  readonly settings: BufferedCacheSettings;
  readonly frames: Frame[];
}

export interface Frame {
  readonly datetime: Date;
  readonly isDownloading: boolean;
  readonly previousReadyFrame?: number;
  readonly nextReadyFrame?: number;
  readonly variableData?: Record<string, VariableImageData>;
}

export interface VariableImageData {
  readonly texture: TextureData;
  readonly imageType: 'VECTOR' | 'SCALAR';
  readonly imageUnscale: [number, number];
  readonly bounds: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
}

export function putInCache(
  cache: BufferedFrameCache,
  index: number,
  data: Record<string, VariableImageData>,
): BufferedFrameCache {
  const pastInterpolateStart = Math.max(0, 1 + index - cache.settings.keyFrameInterval);
  const futureInterpolateEnd = Math.min(
    cache.frames.length,
    index + cache.settings.keyFrameInterval,
  );

  // The majority of frames are unaffected, so get rid of these in any loops
  const unaffectedFramesBefore = cache.frames.slice(0, pastInterpolateStart);
  const unaffectedFramesAfter = cache.frames.slice(futureInterpolateEnd, cache.frames.length);

  const affectedFrames = cache.frames
    .slice(pastInterpolateStart, futureInterpolateEnd)
    .map((val, iRelative) => {
      // Add offset to get absolute index of this frame
      const i = iRelative + pastInterpolateStart;

      // Check if this frame can use the new frame data as an interpolation target
      if (i < index) {
        const hasBetterKeyFrame =
          val.variableData != null || (val.nextReadyFrame != null && val.nextReadyFrame < index);
        if (hasBetterKeyFrame) {
          return val;
        }
        return {
          ...val,
          nextReadyFrame: index,
        };
      }

      // Check if this frame can use the new frame data as an interpolation source
      if (i > index) {
        const hasBetterKeyFrame =
          val.variableData != null ||
          (val.previousReadyFrame != null && val.previousReadyFrame > index);
        if (hasBetterKeyFrame) {
          return val;
        }
        return {
          ...val,
          previousReadyFrame: index,
        };
      }

      // This is the new frame
      return {
        ...val,
        previousReadyFrame: undefined,
        nextReadyFrame: undefined,
        isDownloading: false,
        variableData: data,
      };
    });

  return {
    ...cache,
    frames: [...unaffectedFramesBefore, ...affectedFrames, ...unaffectedFramesAfter],
  };
}

export function removeFromCache(cache: BufferedFrameCache, index: number): BufferedFrameCache {
  const pastInterpolateStart = Math.max(0, 1 + index - cache.settings.keyFrameInterval);
  const futureInterpolateEnd = Math.min(
    cache.frames.length,
    index + cache.settings.keyFrameInterval,
  );

  const unaffectedFramesBefore = cache.frames.slice(0, pastInterpolateStart);
  const unaffectedFramesAfter = cache.frames.slice(futureInterpolateEnd, cache.frames.length);

  const affectedFrames = cache.frames
    .slice(pastInterpolateStart, futureInterpolateEnd)
    .map((val, iRelative) => {
      const i = iRelative + pastInterpolateStart;

      // Frames before the removed index may have nextReadyFrame pointing to it
      if (i < index) {
        if (val.nextReadyFrame !== index) {
          return val;
        }

        // Find the next nearest future keyframe within the interval (excluding the removed index)
        const maxJ = Math.min(cache.frames.length - 1, i + cache.settings.keyFrameInterval - 1);
        let replacement: number | undefined = undefined;
        for (let j = i + 1; j <= maxJ; j += 1) {
          if (j === index) continue;
          if (cache.frames[j].variableData != null) {
            replacement = j;
            break; // nearest future keyframe
          }
        }

        return {
          ...val,
          nextReadyFrame: replacement,
        };
      }

      // Frames after the removed index may have previousReadyFrame pointing to it
      if (i > index) {
        if (val.previousReadyFrame !== index) {
          return val;
        }

        // Find the nearest previous keyframe within the interval (excluding the removed index)
        const minJ = Math.max(0, i - cache.settings.keyFrameInterval + 1);
        let replacement: number | undefined = undefined;
        for (let j = i - 1; j >= minJ; j -= 1) {
          if (j === index) continue;
          if (cache.frames[j].variableData != null) {
            replacement = j;
            break; // nearest previous keyframe
          }
        }

        return {
          ...val,
          previousReadyFrame: replacement,
        };
      }

      // i === index: clear keyframe data and reset flags
      return {
        ...val,
        isDownloading: true,
        variableData: undefined,
        previousReadyFrame: undefined,
        nextReadyFrame: undefined,
      };
    });

  return {
    ...cache,
    frames: [...unaffectedFramesBefore, ...affectedFrames, ...unaffectedFramesAfter],
  };
}

export function startBuffering(cache: BufferedFrameCache, index: number): BufferedFrameCache {
  if (cache.frames[index].isDownloading) {
    return cache;
  }

  return {
    ...cache,
    frames: cache.frames.map((frame, i) => {
      if (i !== index) {
        return frame;
      }
      return {
        ...frame,
        isDownloading: true,
      };
    }),
  };
}

export function readyForPlayback(frame: Frame): boolean {
  return (
    frame.variableData != null || (frame.previousReadyFrame != null && frame.nextReadyFrame != null)
  );
}
